import { useCallback } from 'react';
import { IAuditLogEntry } from '@skystone/shared';
import { loginActivityApi } from '@/services/login-activity-api';
import { formatDateTime } from '@/lib/date-utils';
import { Badge, PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import { useServerPaginatedQuery } from '@/hooks/useServerPaginatedQuery';
import { PageContainer } from '@/components/layout';

const DEFAULT_PAGE_SIZE = 20;

function renderMethod(value: unknown): React.ReactNode {
  const entry = value as Record<string, unknown> | null;
  const method = entry?.method as string | undefined;
  if (method === 'sso') {
    return <Badge variant="secondary">SSO</Badge>;
  }
  return <Badge variant="default">Local</Badge>;
}

const columns: ColumnDef<IAuditLogEntry>[] = [
  {
    id: 'createdAt',
    header: 'Timestamp',
    accessor: 'createdAt',
    cell: (val) => formatDateTime(val as string),
  },
  {
    id: 'actorUsername',
    header: 'User',
    accessor: 'actorUsername',
    filter: { type: 'text', placeholder: 'Filter user...' },
  },
  {
    id: 'method',
    header: 'Method',
    accessor: 'newValue',
    cell: (val) => renderMethod(val),
  },
];

export function AdminLoginActivityPage() {
  const fetcher = useCallback(
    ({ page, pageSize, filters }: { page: number; pageSize: number; filters: Record<string, string> }) =>
      loginActivityApi.getLogins({
        page,
        pageSize,
        actorUsername: filters.actorUsername || undefined,
      }),
    [],
  );

  const { data, loading, error, serverPagination, handleFilterChange, showInitialLoading } =
    useServerPaginatedQuery(fetcher, DEFAULT_PAGE_SIZE, 'Failed to load login activity');

  if (showInitialLoading) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Login Activity"
        subtitle="View user sign-in history."
      />

      <ErrorBanner message={error} />

      <DataTable
        data={data}
        columns={columns}
        keyExtractor={(entry) => entry.id}
        isLoading={loading}
        emptyMessage="No login activity found."
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        enablePagination={false}
        serverPagination={serverPagination}
        enableFilters={true}
        onFilterChange={handleFilterChange}
      />
    </PageContainer>
  );
}
